import React from 'react';
import Box from '@material-ui/core/Box';
import { withRouter } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MenuItem, FormControl, Select, ListSubheader } from '@mui/material';
import { connect } from 'react-redux';

// core components
import routes from '../routes.js';
import { getWorkspacePath } from '../utils/workspaceUtils.js';

class TitleBar extends React.Component {
    constructor(props) {
        super(props);
        // Filter out workspace-prefixed routes to avoid duplicates
        const baseRoutes = routes.filter(route => !route.workspacePrefixed);
        this.state = {
            routesState: baseRoutes,
            groups: [],
            searchStatusDetails: "",
            openStatusDetails: false,
        };
    }
    componentDidMount() {
        let role = localStorage.getItem('role');
        // Filter out workspace-prefixed routes to avoid duplicates
        const baseRoutes = routes.filter(route => !route.workspacePrefixed);
        let navRoutes = baseRoutes;
        if (role == 'agent') {
            // Reason: legacy route names; kept for agent-role visibility filtering
            let remove = ['Campaign', 'Users'];
            navRoutes = baseRoutes.filter(({ name }) => !remove.includes(name));
            this.setState({ routesState: navRoutes });
        }
        this.setState({ groups: buildGroups(navRoutes) });
    }

    // Helper function to get the display name for a route (handles Admin Panel/Agency Portal logic)
    getRouteDisplayName = (route) => {
        if (!route) return '';

        // Check if it's the admin panel route and user has agency subscription
        if (route.path === '/admin') {
            const { currentWorkspace } = this.props;
            const isAgency = currentWorkspace?.['currentSubscription']?.['planId']?.['planType'] === 'agency';
            return isAgency ? 'Agency Portal' : route.name;
        }
        return route.name;
    }

    // Longest-path match wins so child pages (e.g. /activities/notes) resolve to their own entry
    getActiveItem = () => {
        const pathname = this.props.location?.['pathname'] || '';
        const candidates = [];
        this.state.groups.forEach(group => candidates.push(...group['items']));
        this.state.routesState.forEach(route => {
            if (route['name'] && route['path'] && route['path'] !== '/') candidates.push({ name: this.getRouteDisplayName(route), path: route['path'] });
        });
        let best = null;
        candidates.forEach(item => { if (pathname.indexOf(item['path']) > -1 && (!best || item['path'].length > best['path'].length)) best = item; });
        return best;
    }

    handleItemClick = (item) => {
        const workspacePath = getWorkspacePath(item['path'], this.props.currentWorkspace?._id);
        this.props.history.push(workspacePath);
        this.setState({ openStatusDetails: false, searchStatusDetails: '' });
    }

    render() {
        const { openStatusDetails, searchStatusDetails } = this.state;
        const activeItem = this.getActiveItem();
        const term = searchStatusDetails.trim().toLowerCase();
        const visibleGroups = this.state.groups
            .map(group => ({ ...group, items: group['items'].filter(item => !term || item['name'].toLowerCase().includes(term)) }))
            .filter(group => group['items'].length > 0);
        return (
            <div className='page-header-new style-heading'>
                <h2>
                    <div className='filter-box'>
                        <FormControl fullWidth>
                            <div className='heading-dropdown-area'>
                                <Select
                                    displayEmpty
                                    id='header-dropdown'
                                    open={openStatusDetails}
                                    className='header-dropdown'
                                    /* Reason: Select clones MenuItems and derives their `selected` from value equality — a hand-set selected prop is overwritten */
                                    value={activeItem ? activeItem['path'] : ''}
                                    onOpen={() => this.setState({ openStatusDetails: true })}
                                    onClose={() => this.setState({ openStatusDetails: false, searchStatusDetails: '' })}
                                    IconComponent={ExpandMoreIcon}
                                    renderValue={() => (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <h2>{activeItem ? activeItem['name'] : ''}</h2>
                                        </Box>
                                    )}
                                    /* Reason: variant 'menu' + explicit origins pin the paper directly below the trigger — the default 'selectedMenu' shifts it to overlay the selected item, which pushed it over the sidebar */
                                    MenuProps={{ disableAutoFocusItem: true, autoFocus: false, variant: 'menu', anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { className: 'header-dropdown-menu' } }}
                                >
                                    <ListSubheader className='header-dd-search'>
                                        <div className='header-dd-search-box'>
                                            <i className='fa fa-search header-dd-search-icon'></i>
                                            <TextField
                                                fullWidth
                                                autoFocus
                                                hiddenLabel
                                                variant='standard'
                                                placeholder='Search pages...'
                                                value={searchStatusDetails}
                                                InputProps={{ disableUnderline: true }}
                                                onChange={(e) => this.setState({ searchStatusDetails: e['target']['value'] })}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </ListSubheader>
                                    {visibleGroups.length > 0 ? visibleGroups.flatMap((group) => ([
                                        <ListSubheader disableSticky className='header-dd-group' key={`group-${group['label']}`}>{group['label']}</ListSubheader>,
                                        ...group['items'].map((item) => (
                                            <MenuItem
                                                key={item['path']}
                                                value={item['path']}
                                                onClick={() => this.handleItemClick(item)}
                                            >
                                                <i className='header-dd-item-icon'>{item['icon']}</i> {item['name']}
                                            </MenuItem>
                                        ))
                                    ])) : <MenuItem disabled>No results found</MenuItem>}
                                </Select>
                            </div>
                        </FormControl>
                    </div>
                </h2>
            </div>
        );
    }
}

// Menu grouping — route names per section; unclaimed nav routes fall into "More" so new pages never vanish
const GROUP_DEFS = [
    { label: 'General', names: ['Dashboard', 'Campaigns', 'Inbox'] },
    { label: 'CRM', names: ['Deals', 'Contacts', 'Leads', 'Companies'] },
    { label: 'Activities', names: ['Notes', 'Emails', 'Calls', 'SMS'] },
    { label: 'Workspace', names: ['Agents', 'Workflows', 'Accounts'] },
];

function buildGroups(baseRoutes) {
    const byName = {};
    const claimed = new Set(GROUP_DEFS.flatMap(({ names }) => names));
    // Reason: `navigation` governs dropdown membership; `hidden` only governs the sidebar rail (see routes.js Notes/Emails/Calls/SMS)
    baseRoutes.forEach(route => { if (route['navigation'] && route['name']) byName[route['name']] = route; });
    const groups = GROUP_DEFS.map(({ label, names }) => ({
        label,
        items: names.filter(name => byName[name]).map(name => ({ name, path: byName[name]['path'], icon: byName[name]['icon'] }))
    }));
    const leftover = Object.values(byName).filter(route => !claimed.has(route['name']));
    if (leftover.length > 0) groups.push({ label: 'More', items: leftover.map(route => ({ name: route['name'], path: route['path'], icon: route['icon'] })) });
    return groups.filter(group => group['items'].length > 0);
}

const mapStateToProps = ({ Settings: { currentWorkspace } }) => ({ currentWorkspace });
export default connect(mapStateToProps)(withRouter(TitleBar));
